import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useSuspenseQuery} from '@github-ui/react-query'
import {LabelCountsQuery} from '@github-ui/server-persisted-queries'
import type {LabelCountsQueryQuery} from '@github-ui/server-persisted-queries/types/LabelCountsQuery'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {AlertIcon, GitPullRequestIcon, IssueOpenedIcon} from '@primer/octicons-react'
import {Link, SkeletonBox, Tooltip, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense, useCallback} from 'react'

import type {LabelCountsNode} from './label-counts-query-types'
import styles from './RepositoryLabel.module.css'
import {TEMP_LABEL_ID_PREFIX} from './utils/label-connection-operations'

type IssuesAndPullRequestsCountProps = {
  labelName: string
  labelId: string
  labelIds: string[]
  repositoryNameWithOwner: string
}

export const IssuesAndPullRequestsCount = ({
  labelName,
  labelId,
  labelIds,
  repositoryNameWithOwner,
}: IssuesAndPullRequestsCountProps) => {
  const {isBrowser} = useRuntimeEnvironment()

  if (labelIds.length === 0) return null

  // `verifiedFetchJSON` reads `window.location` and `useSuspenseQuery` runs its
  // queryFn during render, so the fetch would execute (and crash) during SSR.
  // Render the loading fallback on the server and only fetch in the browser.
  if (!isBrowser) return <LoadingLabelSecondaryData />

  return (
    <ErrorBoundary
      boundaryName="LabelsPage"
      fallback={
        <ListItemDescriptionItem className={styles.labelRowIssuesAndPrsCount}>
          <AlertIcon size={16} /> Could not load data
        </ListItemDescriptionItem>
      }
    >
      <Suspense fallback={<LoadingLabelSecondaryData />}>
        <IssuesAndPullRequestsCountFetched
          labelName={labelName}
          labelId={labelId}
          labelIds={labelIds}
          repositoryNameWithOwner={repositoryNameWithOwner}
        />
      </Suspense>
    </ErrorBoundary>
  )
}

const LoadingLabelSecondaryData = () => {
  return (
    <ListItemDescriptionItem className={styles.labelRowIssuesAndPrsCount}>
      <SkeletonBox className={styles.loadingIssueAndPullRequestCount} />
      <SkeletonBox className={styles.loadingIssueAndPullRequestCount} />
    </ListItemDescriptionItem>
  )
}

const IssuesAndPullRequestsCountFetched = ({
  labelName,
  labelId,
  labelIds,
  repositoryNameWithOwner,
}: IssuesAndPullRequestsCountProps) => {
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister -- persister can be added later if we need durable caching and/or runtime validation for this endpoint
  const {data} = useSuspenseQuery({
    queryKey: ['repository-label', 'counts', {repositoryNameWithOwner, labelIds}],
    queryFn: async ({signal}) => {
      const params = new URLSearchParams()
      params.append('queryId', LabelCountsQuery)
      for (const id of labelIds) {
        if (!id.startsWith(TEMP_LABEL_ID_PREFIX)) {
          params.append('ids[]', id)
        }
      }
      const response = await verifiedFetchJSON(`/${repositoryNameWithOwner}/labels/counts?${params}`, {signal})
      if (!response.ok) throw new Error(`Label counts fetch failed (${response.status})`)
      const json = (await response.json()) as {data?: LabelCountsQueryQuery; errors?: unknown[]}
      if (!json.data) throw new Error('Label counts query returned no data')
      return json.data
    },
  })

  const labelNode = data.nodes.find(
    (node): node is LabelCountsNode => node !== null && 'issueCount' in node && node.id === labelId,
  )

  if (!labelNode) return null

  return (
    <IssuesAndPullRequestsCountInternal
      repositoryNameWithOwner={repositoryNameWithOwner}
      labelName={labelName}
      labelNode={labelNode}
    />
  )
}

export const IssuesAndPullRequestsCountInternal = ({
  labelName,
  labelNode,
  repositoryNameWithOwner,
}: {
  labelName: string
  labelNode: Pick<LabelCountsNode, 'issueCount' | 'pullRequestCount'>
  repositoryNameWithOwner: string
}) => {
  const getLink = useCallback(
    (type: 'issue' | 'pr') => {
      const query = `${encodeURIComponent(`is:open is:${type} label:"${labelName}"`)}`
      return `/${repositoryNameWithOwner}/issues?q=${query}`
    },
    [repositoryNameWithOwner, labelName],
  )

  const openIssues = labelNode.issueCount || 0
  const openPullRequests = labelNode.pullRequestCount || 0

  const hasPullRequest = openPullRequests > 0
  const hasIssues = openIssues > 0

  return (
    <ListItemDescriptionItem className={styles.labelRowIssuesAndPrsCount}>
      <div className={clsx(styles.countContainer, !hasPullRequest && styles.empty)}>
        {hasPullRequest && (
          <Tooltip text={`${openPullRequests} open pull requests`} direction="n">
            <Link href={getLink('pr')} muted className={styles.countItem}>
              <GitPullRequestIcon size={16} />
              {openPullRequests} <VisuallyHidden>open pull requests</VisuallyHidden>
            </Link>
          </Tooltip>
        )}
      </div>
      <div className={clsx(styles.countContainer, !hasIssues && styles.empty)}>
        {hasIssues && (
          <Tooltip text={`${openIssues} open issues`} direction="n">
            <Link href={getLink('issue')} muted className={styles.countItem}>
              <IssueOpenedIcon size={16} />
              {openIssues} <VisuallyHidden>open issues</VisuallyHidden>
            </Link>
          </Tooltip>
        )}
      </div>
    </ListItemDescriptionItem>
  )
}
